import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CanonicalService } from '../../../shared/seo/canonical.service';

@Component({
  selector: 'app-transactional-emails',
  templateUrl: './transactional-emails.component.html',
  styleUrls: ['./transactional-emails.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
   encapsulation: ViewEncapsulation.None  // ✅ Make sure this line is here
})
export class TransactionalEmailsComponent implements OnInit {
  currency: 'USD' | 'INR' = 'USD';
  isSelectOpen = false;

  toggleSelect() {
    this.isSelectOpen = !this.isSelectOpen;
  }

  // For Plus Plan (unique logic)
  isPlusSelectOpen = false;

  togglePlusSelect() {
    this.isPlusSelectOpen = !this.isPlusSelectOpen;
  }

  USD_PRICES = {
    starter: { '50000': 20, '100000': 35 } as Record<string, number>,
    plus: { '100000': 90, '300000': 249, '700000': 449 } as Record<
      string,
      number
    >,
    elite: 13500,
  };

  INR_RATE = 96.00;

  USD_OVERAGE_MAIL_PRICE = {
    starter: { '50000': 0.00133, '100000': 0.00088 } as Record<string, number>,
    plus: { '100000': 0.0011, '300000': 0.00091, '700000': 0.00078 } as Record<
      string,
      number
    >,
  };

  starterSelectedVolume: string = '50000';
  plusSelectedVolume: string = '100000';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private canonicalService: CanonicalService
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle(
      'Transactional Email | Reliable APIs by MALtech'
    );
    this.metaService.updateTag({
      name: 'description',
      content:
        'MALtech is a cloud-based SMTP provider for sending transactional emails using flexible APIs—no need to manage your own email servers.',
    });
    this.metaService.updateTag({
      name: 'keywords',
      content: 'Transactional Email, SMTP, Email API, MALtech',
    });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });

    this.canonicalService.setCanonicalURL('product/transactional-emails/');
  }

  setCurrency(selected: 'USD' | 'INR') {
    this.currency = selected;
  }

  getPlanPrice(plan: string, volume: string): string {
    let price = '';
    if (plan === 'starter') {
      price = this.getConvertedPrice(this.USD_PRICES.starter[volume]);
    } else if (plan === 'plus') {
      price = this.getConvertedPrice(this.USD_PRICES.plus[volume]);
    } else if (plan === 'elite') {
      price = this.getConvertedPrice(this.USD_PRICES.elite);
    }
    return this.currency === 'USD' ? `$${price}/month` : `₹${price}/month`;
  }

getOverageMailPrice(plan: string, volume: string): string {
  let price = 0;

  if (plan === 'starter' && this.USD_OVERAGE_MAIL_PRICE.starter[volume]) {
    price = this.USD_OVERAGE_MAIL_PRICE.starter[volume];
  } else if (plan === 'plus' && this.USD_OVERAGE_MAIL_PRICE.plus[volume]) {
    price = this.USD_OVERAGE_MAIL_PRICE.plus[volume];
  }

  if (this.currency === 'INR') {
    const inr = price * this.INR_RATE;
    return `₹${parseFloat(inr.toFixed(2))}`; // round and remove trailing zeros
  }

  return `$${price.toFixed(5)}`; // round and remove trailing zeros
}


  getConvertedPrice(usd: number): string {
    if (this.currency === 'USD') return usd.toString();
    return Math.round(usd * this.INR_RATE).toLocaleString();
  }

  goToSignup(): void {
    try {
      console.log('Attempting to navigate to signup page');
      window.location.href = 'https://maltech.co/signup/';
    } catch (error) {
      console.error('Failed to navigate:', error);
    }
  }
  scrollToPlans() {
    const plansSection = document.getElementById('email-plans');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
  supports = [
    {
      title: '24/7 Support',
      icon: 'bi-clock',
      link: '/contact',
      newTab: false,
    },
    {
      title: 'Phone Support',
      icon: 'bi-telephone',
      link: 'tel:+6369765570',
      newTab: false,
    },
    {
      title: 'Email Support',
      icon: 'bi-envelope',
      link: 'https://mail.google.com/mail/?view=cm&fs=1&to=support@maltech.co',
      newTab: true,
    },
    {
      title: 'WhatsApp Support',
      icon: 'bi-whatsapp', // official WhatsApp icon
      link: 'https://wa.me/6369765570',
      newTab: true,
    },
  ];

  features = [
    {
      title: 'Smart Integration for Smarter Communication',
      description: `Empower your systems with MALtech's streamlined SMTP relay and flexible Web & SMTP APIs. 
      Customize your email workflows effortlessly while maintaining full control over delivery, tracking, and analytics.`,
      image: '/assets/images/transactionalemail/transactional1.webp',
    },
    {
      title: 'Real-Time Insights & Analytics',
      description: `Enable marketers to take full ownership of their content and campaign performance with MALtech Transactional Email's intuitive user interface. With complete feature parity between the UI and the powerful Web API, developers can unlock advanced capabilities effortlessly—achieving more with just a few lines of code.`,
      image: '/assets/images/transactionalemail/transactional2.webp',
    },
    {
      title: 'Scalable & Reliable Infrastructure',
      description: `Navigate your email strategy with confidence using MALtech's self-serve support ecosystem. Access detailed documentation, a rich knowledge base, and step-by-step guides tailored to your needs. Whether you're troubleshooting, optimizing, or scalingâ€”our resources empower you to move forward without delay. Plus, join a vibrant community of users and experts ready to help when you need it most.`,
      image: '/assets/images/transactionalemail/transactional4.webp',
    },
    {
      title: 'Scalable & Reliable Infrastructure',
      description: `MALtech Transactional Email offers a flexible infrastructure that effortlessly scales with your business. As your email volume fluctuates, it adjusts dynamically, removing the complexities of predicting, provisioning, or managing server capacity.`,
      image: '/assets/images/transactionalemail/transactional3.webp',
    },
  ];

 brands = [
  {
    name: 'Crompton',
    logo: 'assets/images/customers/Healthi_logo-p-500.webp',
    type: 'light'
  },
  {
    name: 'Atqor',
    logo: 'assets/images/customers/Croma_Logo_acrkvn.webp',
    type: 'dark'
  },
  {
    name: 'Exabytes',
    logo: 'assets/images/customers/alyve.webp',
    type: 'light'
  }
];

}
